<main class="text-start">
  <h1 class="page-title">
    <a [routerLink]="['/users']" class="text-[#8A8AA3] me-0.5 ms-2 hover:text-purple-800">
      <i class="bi" [class.bi-chevron-right]="languageService.isRtl()" [class.bi-chevron-left]="!languageService.isRtl()"></i>
      {{ 'users_management.details.back' | translate }}
    </a>
    {{ 'users_management.details.title' | translate }}
  </h1>

  <div class="w-full bg-[#F6F6F6] rounded-lg p-3.5 space-y-3.5">

    <!-- Profile header -->
    <div *ngIf="loading" class="bg-white rounded-xl p-5 flex max-md:flex-col-reverse items-center gap-5">
      <div class="w-24 h-24 rounded-full bg-gray-200 animate-pulse shrink-0"></div>
      <div class="flex-1 w-full text-start space-y-2">
        <div class="h-5 w-1/3 bg-gray-200 rounded animate-pulse ms-auto"></div>
        <div class="h-3 w-1/4 bg-gray-200 rounded animate-pulse ms-auto"></div>
      </div>
      <div class="h-9 w-32 bg-gray-200 rounded-lg animate-pulse md:ms-auto"></div>
    </div>

    <div *ngIf="!loading && user" class="bg-white rounded-xl p-5 flex max-md:flex-col-reverse items-center gap-5">
      <div class="w-24 h-24 rounded-full overflow-hidden bg-[#FCC5F3] shrink-0">
        <img
          [src]="user.user.profile_image || 'dashboard/place-holders/avatar-female.png'"
          class="w-full h-full object-cover"
          alt="" />
      </div>

      <div class="flex-1 w-full text-start" [class.md:flex-initial]="isOwner">
        <p class="text-lg font-semibold text-[#121217]">{{ user.user.name }}</p>
        <p class="text-sm text-[#6C6C89] mt-0.5">{{ user.user.email }}</p>
        <ng-container *ngIf="isOwner; else statusChip">
          <div class="inline-flex items-center gap-2 mt-2">
            <button type="button"
              [disabled]="isCurrentUser(user.user.id) || togglingActive"
              (click)="toggleActive()"
              class="relative inline-flex items-center select-none cursor-pointer disabled:cursor-wait">
              <div class="w-8 h-4 rounded-full transition-colors duration-300"
                [ngClass]="user.user.is_active ? 'bg-green-500' : 'bg-gray-300'">
              </div>
              <div class="absolute start-0.5 top-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-300"
                [ngStyle]="{ transform: user.user.is_active ? (languageService.isRtl() ? 'translateX(-16px)' : 'translateX(16px)') : 'translateX(0)' }">
              </div>
            </button>
            <span class="text-[0.7rem]"
              [ngClass]="user.user.is_active ? 'text-green-700' : 'text-gray-500'">
              {{ (user.user.is_active ? 'users_management.list.status.active' : 'users_management.list.status.inactive') | translate }}
            </span>
          </div>
        </ng-container>
        <ng-template #statusChip>
          <span class="mt-2 inline-block px-3 py-1 rounded-full text-[0.6rem]"
            [ngClass]="user.user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
            {{ (user.user.is_active ? 'users_management.list.status.active' : 'users_management.list.status.inactive') | translate }}
          </span>
        </ng-template>
      </div>

      <div *ngIf="isOwner" class="md:ms-auto md:self-start">
        <a [routerLink]="['/users', user.id, 'edit']"
          class="inline-flex items-center gap-2 whitespace-nowrap bg-[#611F79] hover:bg-[#4d1860] text-white text-xs px-4 h-9 rounded-lg transition-colors">
          {{ 'users_management.details.edit_button' | translate }} <i class="bi bi-pencil-square"></i>
        </a>
      </div>
    </div>

    <!-- Personal info -->
    <div *ngIf="loading" class="bg-white rounded-xl p-5 space-y-4">
      <div class="h-4 w-32 bg-gray-200 rounded animate-pulse me-auto"></div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div *ngFor="let _ of [1,2,3,4]" class="space-y-2 flex flex-col items-start">
          <div class="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>

    <div *ngIf="!loading && user" class="bg-white rounded-xl p-5">
      <p class="text-sm font-semibold mb-4">{{ 'users_management.details.personal_section' | translate }}</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.first_name_ar' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.first_name_ar || '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.last_name_ar' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.last_name_ar || '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.first_name' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.first_name || '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.last_name' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.last_name || '—' }}</p>
        </div>
      </div>
    </div>

    <!-- Contact + account info -->
    <div *ngIf="loading" class="bg-white rounded-xl p-5 space-y-4">
      <div class="h-4 w-32 bg-gray-200 rounded animate-pulse me-auto"></div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div *ngFor="let _ of [1,2,3,4]" class="space-y-2 flex flex-col items-start">
          <div class="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>

    <div *ngIf="!loading && user" class="bg-white rounded-xl p-5">
      <p class="text-sm font-semibold mb-4">{{ 'users_management.details.contact_section' | translate }}</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.phone' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.phone_number ?? '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.email' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.email }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.date_joined' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ user.user.date_joined | date: 'medium' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'users_management.details.field.role' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ ('users_management.roles.' + user.role) | translate }}</p>
        </div>
      </div>
    </div>

  </div>
</main>
